// "Add from Hey Captain library": the one engine behind scripts/import-ra.mjs, the Risk Assessments page's picker
// (src/components/LibraryPicker.jsx) and the AI's library drafts - for any library kind, not only risk assessments.
// Plain JS, no fetching: the caller gives what the vessel already has and a small write adapter. Tests: tests/ra-library.test.mjs,
// tests/library-add.test.mjs.
//
// A library kind (src/lib/raLibrary.js RA_LIBRARY is the first; contingency plans add theirs the same way):
//   key, label, table          the records' table (risk_assessments, later contingency_plans ...)
//   items(lib)                 the library's entries
//   itemKey(item) / rowKey(r)  [code, title] of a library entry / of a vessel row: the same pair = already on the vessel
//   map(item, today, ctx)      -> { row, checklist | null }: the vessel row (no client_id / link) and its checklist template; ctx = the
//                                 vessel's context ({ regs, flag } for risk assessments) plus lib (the whole library file)
//                                 ({ title, description, category, source, items: [{ step_order, title, required, is_... }] })
//   linkColumn                 the row's column that points at the checklist (checklist_id), when the kind has checklists
//   existingSelect             the columns planAdd needs from the vessel's rows
//   createdBy                  checklists.created_by of a checklist made here
//   card(item)                 { code, title, lines: [...] } for the picker's list
//   updatable(row) / content(item, ctx)   (optional, --update) which vessel rows a newer library version may overwrite, and the
//                                 fields it writes (never status, dates, the review or the checklist link)
// Optional (a library kept in the database, e.g. contingency plans - src/lib/contingency.js CONTINGENCY_LIBRARY):
//   match(item, rows)          the vessel row that IS this entry (instead of the [code, title] pair), or null
//   unfinished(row)            why a matched row still needs the add (a copy without steps, not linked yet ...): text, or false
//   add(api, entry, clientId)  writes one entry itself (e.g. api.rpc of a database function) instead of the inserts below
//   preview(item)              [lines] shown by the picker's Preview (the hazards / steps of the entry)
//   verb, addedNote            the picker's words: 'Add' (default) / 'Adopt'; what the result says after "Added n"
// Idempotent, as the import script always was: an entry whose [code, title] the vessel has is skipped, a checklist of the same
// title is reused (a header left with no steps by a run that stopped half way gets its steps), never a second copy.

const txt = v => String(v == null ? '' : v).trim()
const pairKey = p => txt(p[0]).toLowerCase() + '|' + txt(p[1]).toLowerCase()

// rows for one bulk insert: the union of the keys, every row carrying all of them (missing = null) - PostgREST refuses a bulk
// insert whose rows have different keys (PGRST102)
export function uniformRows(rows) {
  const keys = [...new Set(rows.flatMap(r => Object.keys(r)))]
  return rows.map(r => Object.fromEntries(keys.map(k => [k, r[k] === undefined ? null : r[k]])))
}

// existing = { rows: [vessel rows with existingSelect], checklists: [{ id, title, items?: number of steps }] } -> one entry per
// library item: { code, title, action 'create' | 'skip', checklist 'create' | 'reuse' | null, checklistId, fillItems, row, list, item }
export function planAdd(kind, lib, existing, today, ctx = {}) {
  const rows = existing.rows || []
  const have = new Set(rows.map(r => pairKey(kind.rowKey(r))))
  const lists = new Map((existing.checklists || []).map(c => [txt(c.title).toLowerCase(), c]))
  return kind.items(lib).map(item => {
    const m = kind.map(item, today, { ...ctx, lib }), [code, title] = kind.itemKey(item)
    const cl = m.checklist ? lists.get(txt(m.checklist.title).toLowerCase()) : null
    const found = kind.match ? kind.match(item, rows) : (have.has(pairKey([code, title])) ? true : null)
    const why = found && found !== true && kind.unfinished ? kind.unfinished(found) : false
    return { code, title, action: found ? 'skip' : 'create', checklist: m.checklist ? (cl ? 'reuse' : 'create') : null,
      checklistId: cl ? cl.id : null, fillItems: (!!cl && Number(cl.items) === 0) || !!why, unfinished: why || null, row: m.row, list: m.checklist, item }
  })
}
// the entries to write (picked = codes the user ticked; none = every entry to create)
export const toWrite = (plan, picked) => plan.filter(p => (p.action === 'create' || p.fillItems) && (!picked || picked.includes(p.code)))

// api = { insert(table, body, { returning }) -> rows | null }, written as the caller (the script: service key, the page: the login)
// -> { made: [codes], filled: [codes], skipped: [{ code, why }], failed: [{ code, error }], notDone: [codes] }. The first failure stops the
// run (as the import script always did): what is written stays, and the next run picks up exactly where it stopped (idempotent)
export async function runAdd(kind, entries, clientId, api, onEntry) {
  const out = { made: [], filled: [], skipped: [], failed: [], notDone: [] }
  for (const [k, p] of entries.entries()) {
    try {
      if (kind.add) {                                   // the kind writes the entry itself (a database function)
        await kind.add(api, p, clientId)
        if (p.action === 'create') out.made.push(p.code); else out.filled.push(p.code)
        if (onEntry) onEntry(p, null)
        continue
      }
      let checklistId = p.checklistId
      if (p.list && !checklistId) {
        const [c] = await api.insert('checklists', { client_id: clientId, title: p.list.title, description: p.list.description, category: p.list.category, source: p.list.source, created_by: kind.createdBy, is_template: true }, { returning: true })
        checklistId = c.id
      }
      // a new header, or one left without steps: every step with the same keys (one bulk insert)
      if (p.list && (!p.checklistId || p.fillItems)) await api.insert('checklist_items', uniformRows(p.list.items.map(i => ({ ...i, checklist_id: checklistId }))))
      if (p.action === 'create') {
        try { await insertKnown(api, kind.table, { ...p.row, client_id: clientId, ...(kind.linkColumn && checklistId ? { [kind.linkColumn]: checklistId } : {}) }) }
        catch (e) {
          // added meanwhile (another device, a second click): the database's unique rule refused the copy - nothing to do
          if (/23505|duplicate key|409/.test(String(e && e.message))) { out.skipped.push({ code: p.code, why: 'already on this vessel' }); continue }
          throw e
        }
        out.made.push(p.code)
      } else out.filled.push(p.code)
      if (onEntry) onEntry(p, null)
    } catch (e) { out.failed.push({ code: p.code, error: String(e && e.message || e) }); out.notDone = entries.slice(k + 1).map(x => x.code); if (onEntry) onEntry(p, e); break }
  }
  return out
}

// a column the database does not have yet (a newer library field before its SQL ran: PostgREST PGRST204 "Could not find the 'x'
// column") is left out and the insert / update tried again - the row still arrives, the field comes with the SQL
const missingColumn = e => (/Could not find the '(\w+)' column/.exec(String(e && e.message)) || [])[1] || null
export async function withKnownColumns(body, run) {
  let b = { ...body }
  for (let k = 0; k < 8; k++) {
    try { return await run(b) } catch (e) { const c = missingColumn(e); if (!c || !(c in b)) throw e; const { [c]: _, ...rest } = b; b = rest }
  }
  return run(b)
}
const insertKnown = (api, table, body) => withKnownColumns(body, b => api.insert(table, b))

// --update: the vessel rows a newer library version upgrades. rows = the vessel's rows (id, ra_number, activity, source, status, ...)
// matched on (ra_number, activity) = the library's (code, title) on that vessel; only kind.updatable rows (for RAs: source 'library'
// and not Active - an Active RA is the Master's reviewed version and is never changed). -> [{ code, title, id, action 'update' | 'keep', why, before, after }]
export function planUpdate(kind, lib, rows, ctx = {}) {
  const items = new Map(kind.items(lib).map(it => [pairKey(kind.itemKey(it)), it]))
  return (rows || []).filter(r => items.has(pairKey(kind.rowKey(r)))).map(r => {
    const item = items.get(pairKey(kind.rowKey(r))), [code, title] = kind.itemKey(item)
    if (!kind.updatable(r)) return { code, title, id: r.id, action: 'keep', why: String(r.status || '').toLowerCase().trim() === 'active' ? 'Active (reviewed by the Master): not changed' : 'not a library copy (source ' + (r.source || 'manual') + ')', before: r }
    return { code, title, id: r.id, action: 'update', before: r, after: kind.content(item, { ...ctx, lib }) }
  })
}
// api = { update(table, id, body) } -> { updated: [codes], failed: [{ code, error }] } (the first failure stops; a run again is safe)
export async function runUpdate(kind, plan, api) {
  const out = { updated: [], failed: [] }
  for (const p of plan.filter(x => x.action === 'update')) {
    try { await withKnownColumns(p.after, b => api.update(kind.table, p.id, b)); out.updated.push(p.code) }
    catch (e) { out.failed.push({ code: p.code, error: String(e && e.message || e) }); break }
  }
  return out
}

// the registry of library kinds the app knows; contingency plans: add { contingency_plans: CONTINGENCY_LIBRARY } here (their
// branch owns the data, the table and the descriptor) and the same picker / engine add them - nothing else to build
export const LIBRARY_KINDS = {}
export const registerLibrary = kind => { LIBRARY_KINDS[kind.key] = kind; return kind }
